import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getBOMs, createBOM, updateBOM, deleteBOM, getLibraryMaterials } from '../../services/api';
import { toast } from 'sonner';
import { Layers, Plus, Trash2, Search, Pencil } from 'lucide-react';

const TypePill = ({ type }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs ${type === 'profile' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{type || '-'}</span>
);

const BOMLibrary = () => {
  const [boms, setBoms] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('window');
  const [components, setComponents] = useState([]);
  const [libSearch, setLibSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [b, lm] = await Promise.all([getBOMs(), getLibraryMaterials()]);
      setBoms(b.data);
      setLibrary(lm.data);
    } catch (e) { toast.error('Failed to load BOM library'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditId(null); setCode(''); setName(''); setProductType('window'); setComponents([]); setLibSearch('');
    setOpen(true);
  };
  const openEdit = (b) => {
    setEditId(b.id); setCode(b.code); setName(b.name); setProductType(b.product_type || 'window');
    setComponents((b.components || []).map((c) => ({ ...c })));
    setLibSearch('');
    setOpen(true);
  };

  const addComponent = (m) => {
    if (components.some((c) => c.lib_id === m.id)) { toast.info('Already added'); return; }
    const isProfile = m.item_type === 'profile';
    setComponents([...components, {
      lib_id: m.id, item_code: m.item_code || '', item_name: m.item_name,
      item_type: m.item_type || 'hardware', unit: isProfile ? 'm' : (m.unit || 'pcs'), qty_per_unit: 1,
    }]);
    setLibSearch('');
  };
  const setQty = (idx, val) => { const n = components.slice(); n[idx] = { ...n[idx], qty_per_unit: val }; setComponents(n); };
  const removeComp = (idx) => { const n = components.slice(); n.splice(idx, 1); setComponents(n); };

  const save = async () => {
    if (!code.trim()) { toast.error('Enter the door/window code (must match the code used on jobs)'); return; }
    if (!name.trim()) { toast.error('Enter a name'); return; }
    const valid = components.filter((c) => c.item_name && Number(c.qty_per_unit) > 0);
    if (valid.length === 0) { toast.error('Add at least one component with a quantity'); return; }
    setSaving(true);
    const payload = {
      code: code.trim(), name: name.trim(), product_type: productType,
      components: valid.map((c) => ({ lib_id: c.lib_id || null, item_code: c.item_code || '', item_name: c.item_name, item_type: c.item_type || 'hardware', unit: c.unit || 'pcs', qty_per_unit: Number(c.qty_per_unit) })),
    };
    try {
      if (editId) await updateBOM(editId, payload); else await createBOM(payload);
      toast.success(editId ? 'BOM updated' : 'BOM created');
      setOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save BOM');
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete BOM "${b.code} — ${b.name}"?`)) return;
    try { await deleteBOM(b.id); toast.success('BOM deleted'); fetchData(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const filteredLib = library.filter((m) =>
    (m.item_name || '').toLowerCase().includes(libSearch.toLowerCase()) ||
    (m.item_code || '').toLowerCase().includes(libSearch.toLowerCase())
  );
  const libResults = filteredLib.slice(0, 20);

  return (
    <div className="space-y-4" data-testid="bom-library">
      <Card><CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="font-medium flex items-center gap-2"><Layers size={16} className="text-accent" />Bill of Materials</p>
          <p className="text-sm text-muted-foreground">Map each door/window code to its profiles & hardware so pick lists generate fully</p>
        </div>
        <Button className="btn-accent" onClick={openNew} data-testid="new-bom-btn"><Plus size={16} className="mr-1" />New BOM</Button>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" /></div>
        ) : boms.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-muted-foreground"><Layers size={40} className="mb-3 opacity-50" /><p>No BOMs yet</p><p className="text-sm mt-1">Create one per door/window type to auto-generate pick lists</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50"><tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Code</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Components</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
              </tr></thead>
              <tbody className="divide-y">
                {boms.map((b) => (
                  <tr key={b.id} className="hover:bg-muted/30" data-testid={`bom-row-${b.id}`}>
                    <td className="px-4 py-3 font-mono text-sm font-medium">{b.code}</td>
                    <td className="px-4 py-3 text-sm">{b.name}</td>
                    <td className="px-4 py-3"><TypePill type={b.product_type} /></td>
                    <td className="px-4 py-3 text-sm">{(b.components || []).length}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`edit-bom-${b.id}`}><Pencil size={14} /></Button>
                        <Button variant="outline" size="sm" onClick={() => remove(b)} data-testid={`delete-bom-${b.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit BOM' : 'New BOM'}</DialogTitle>
            <DialogDescription>The code must match the door/window code used on production jobs. Search components from your Material Library.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs text-muted-foreground">Code *</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. W1" className="mt-1" data-testid="bom-code" /></div>
            <div className="col-span-1"><Label className="text-xs text-muted-foreground">Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 2-Track Sliding Window" className="mt-1" data-testid="bom-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Product Type</Label>
              <Select value={productType} onValueChange={setProductType}>
                <SelectTrigger className="mt-1" data-testid="bom-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="window">Window</SelectItem><SelectItem value="door">Door</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input placeholder="Search inventory items to add..." value={libSearch} onChange={(e) => setLibSearch(e.target.value)} className="pl-9" data-testid="bom-lib-search" />
          </div>
          {libSearch && (
            <div className="border rounded max-h-40 overflow-y-auto divide-y">
              {filteredLib.length === 0 ? <p className="text-sm text-muted-foreground p-3">No matches in Material Library</p> : libResults.map((m) => (
                <button key={m.id} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/40 text-left" onClick={() => addComponent(m)} data-testid={`bom-add-lib-${m.id}`}>
                  <span><span className="font-mono text-xs mr-2">{m.item_code || '-'}</span>{m.item_name}</span>
                  <TypePill type={m.item_type} />
                </button>
              ))}
            </div>
          )}

          {components.length > 0 && (
            <div className="border rounded overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50"><tr>
                  <th className="px-3 py-2 text-left text-xs font-medium">Component</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">Qty per unit</th>
                  <th className="px-3 py-2 text-left text-xs font-medium">UOM</th>
                  <th className="px-3 py-2"></th>
                </tr></thead>
                <tbody className="divide-y">
                  {components.map((c, idx) => (
                    <tr key={idx} data-testid={`bom-comp-${idx}`}>
                      <td className="px-3 py-2 text-sm">{c.item_name}</td>
                      <td className="px-3 py-2"><TypePill type={c.item_type} /></td>
                      <td className="px-3 py-2"><Input type="number" value={c.qty_per_unit} onChange={(e) => setQty(idx, e.target.value)} className="h-8 w-24" data-testid={`bom-comp-qty-${idx}`} /></td>
                      <td className="px-3 py-2 text-sm">{c.unit}</td>
                      <td className="px-3 py-2"><button onClick={() => removeComp(idx)} className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Button className="w-full btn-accent" onClick={save} disabled={saving} data-testid="save-bom-btn">{saving ? 'Saving…' : (editId ? 'Update BOM' : 'Create BOM')}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BOMLibrary;
